/**
 * Le dossier temporel, tel qu'il se lit — l'onglet « Dossier » de la page patient.
 *
 * Profil, parcours de soins, chronologie de l'épisode déplié, et la barre qui clôt le dossier en un
 * geste. Tout ce qui est clinique ici vient de la projection datée (`useTemporalProjection`) : une
 * valeur n'apparaît jamais avant le document qui l'a produite, et chaque valeur est annotée du jour
 * où le journal la date.
 *
 * Extrait de `routes/patient` parce que DEUX écrans doivent le montrer. Le second est la
 * modale ouverte depuis un jeu de données (`PatientDetailModal`), dont la raison d'être est d'être le
 * reflet de la fiche patient : tant qu'elle rendait le dossier à plat pendant que la spécialité lisait
 * la projection, elle ne montrait pas le même dossier — au point que les pastilles y étaient
 * visuellement inversées. Une pastille évidée dit « la valeur a bougé sous ce geste », et cette
 * question ne se pose pas de la même façon sur les deux surfaces ; la poser depuis deux rendus
 * différents, c'est la poser deux fois et obtenir deux réponses.
 *
 * Ce composant ne charge rien : la page et la modale possèdent déjà la fiche (`/patient/...`) et la
 * projection, et la seconde sert aussi à nommer l'étape du pipeline dans la page.
 */

import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { CalendarClock } from "lucide-react";
import { Trans, useLingui } from "@lingui/macro";
import { Card, CardContent, CardHeader, CardTitle } from "@app/components/ui/card";
import CarePathway, {
  ChronologyList,
  PathwayLastContact,
  PathwaySummary,
} from "@app/components/CarePathway";
import type {
  EpisodeMoment,
  RenderEpisodeReviewFn,
  RenderFieldFn,
} from "@app/components/CarePathway";
import { PatientHeaderCard } from "@app/components/PatientHeaderCard";
import ValidateRemainingCard from "@app/components/ValidateRemainingCard";
import type { FieldGestureResolver } from "@app/components/ValidateRemainingCard";
import ValueAndPropertyDisplay from "@app/components/ValueAndPropertyDisplay";
import ValuePassages from "@app/components/ValuePassages";
import type { Passage } from "@app/components/ValuePassages";
import { isProblemList } from "@app/utils/problem-list";
import { mapFieldToLabel } from "@app/utils/fields";
import { useEventKindLabel } from "@app/utils/care-pathway-labels";
import {
  episodeChronology,
  episodeKey,
  indicationLabel,
  problemFieldKeys,
  profileFieldKeys,
  projectPathway,
} from "@app/utils/care-pathway";
import { valueLabel } from "@app/utils/value-labels";
import type { ChronologyItem } from "@app/utils/care-pathway";
import type { TemporalProjection } from "@app/utils/use-temporal-projection";
import { usePatientQuestionnaires } from "@app/utils/questionnaires";
import { useHiddenFieldKeys } from "@app/zustand/field-visibility";
import type { MageXPatientWithFeedbacks } from "@api/src/types/patient";
import type { PatientEvent } from "@api/src/types/responses";
import type { HeaderLayout, PatientSchema } from "@app/types/few-schot-schema";
import type { MageXPatient } from "@prisma/client";

interface TemporalDossierProps {
  tenantId: string;
  patientId: string;
  /** La version du dossier affichée — identité, en-tête, et la ligne à laquelle un geste s'attache. */
  patient: MageXPatientWithFeedbacks;
  schema: PatientSchema;
  headerLayout: HeaderLayout;
  projection: TemporalProjection;
  /** L'agenda de la source, déjà trié par la page. */
  events: PatientEvent[];
  /** Position du curseur temporel (AAAA-MM-JJ), `null` = aujourd'hui. */
  at: string | null;
  /** The record belongs to at least one dataset. It is then read to JUDGE the extraction, and a
   *  field left empty is precisely what the reader came for: the field selectors open on "Tous"
   *  rather than on "Renseignés". One click restores the usual filter. */
  inDataset?: boolean;
  /** Relire la fiche ET la projection. Appelé après chaque geste. */
  loadPatient: () => void;
}

export default function TemporalDossier({
  tenantId,
  patientId,
  patient,
  schema,
  headerLayout,
  projection,
  events,
  at,
  inDataset = false,
  loadPatient,
}: TemporalDossierProps) {
  const { t } = useLingui();
  const hiddenFieldKeys = useHiddenFieldKeys();
  const eventKindLabel = useEventKindLabel();
  // Questionnaire detail: the projection shows a score at a date, and the arrow shows the answers
  // of the passation behind it.
  const { byField: questionnairesByField } = usePatientQuestionnaires(
    tenantId,
    patientId,
  );

  // Quel épisode est déplié. Trois états et pas deux : `undefined` = le lecteur n'a rien choisi, on
  // ouvre le plus récent ; `null` = il a REPLIÉ, et il ne faut rien rouvrir. Confondre les deux
  // faisait que replier le dernier épisode ouvert le rouvrait aussitôt.
  //
  // Held by `episodeKey`, not by id: each validation rebuilds the pathway and the episodes come back
  // under new ids, so the open one folded after every gesture.
  const [openEpisodeKey, setOpenEpisodeKey] = useState<string | null | undefined>(undefined);

  // Le curseur au repos (null) montre l'état d'aujourd'hui : c'est donc la date que décrivent les
  // corrections faites depuis cette page quand la projection ne date pas le champ.
  const todayIso = useMemo(() => dayjs().format("YYYY-MM-DD"), []);

  const { fields: projectedFields, dates: projectedDates, clinicalKeys } = projection;

  // Processed on this surface: a reading ran, or the projection holds something (demo timelines are
  // seeded without a reading). An unread dossier returns `{}`, not `null`.
  const processedHere =
    !projection.failed &&
    projectedFields !== null &&
    (projection.readAt !== null || Object.keys(projectedFields).length > 0);

  // The patient as displayed, rebuilt PURELY from the dated journal: every clinical field is
  // emptied, then only what the projection supports at the cursor's date is overlaid. Identity
  // fields are not in `clinicalKeys`, so the header keeps them.
  //
  // `Feedbacks` is emptied on purpose: undated values would freeze the fields and the cursor would
  // do nothing. A correction is written INTO the journal at its clinical date, so it is already in
  // the projection, on the right day.
  const displayPatient = useMemo(() => {
    const base: Record<string, unknown> = { ...patient };
    for (const key of clinicalKeys) base[key] = null;
    base.Feedbacks = [];
    return { ...base, ...(projectedFields ?? {}) } as MageXPatientWithFeedbacks;
  }, [patient, projectedFields, clinicalKeys]);

  const projectedEpisodes = useMemo(
    () => projectPathway(projection.pathway, at),
    [projection.pathway, at],
  );
  const openEpisode = useMemo(() => {
    if (projectedEpisodes.length === 0 || openEpisodeKey === null) return null;
    if (openEpisodeKey === undefined) return projectedEpisodes[projectedEpisodes.length - 1];
    // Un épisode choisi puis rendu invisible par le curseur ne reste pas ouvert : il n'existe plus à
    // cette date.
    return projectedEpisodes.find((e) => episodeKey(e.episode) === openEpisodeKey) ?? null;
  }, [projectedEpisodes, openEpisodeKey]);
  const openEpisodeById = (episodeId: string | null) => {
    const chosen = projectedEpisodes.find((e) => e.episode.id === episodeId);
    setOpenEpisodeKey(chosen ? episodeKey(chosen.episode) : null);
  };

  // L'agenda de la source, dans la forme que la chronologie sait dessiner.
  const agendaItems = useMemo<ChronologyItem[]>(
    () =>
      events
        .filter((event) => !!event.debut)
        .map((event, index) => ({
          id: `${event.debut}-${index}`,
          title: event.categorie || event.id_categorie || "—",
          date: event.debut as string,
          note: [event.praticien, event.description].filter(Boolean).join(" · ") || null,
        })),
    [events],
  );

  // What the closing bar sweeps: exactly the fields the patient-level cards draw, « Profil » and
  // « Problèmes et traitements » — a field never shown is never validated in the doctor's name.
  //
  // Les champs d'épisode en sont dehors, et ce n'est pas un oubli : un même champ y est établi par
  // plusieurs moments à plusieurs dates, or le balayage partage un horodatage et la clé du journal
  // est (patient, champ, created_at). « Confirmer la voie d'abord » n'est pas UNE phrase sur un
  // dossier repris, c'en est deux — une par opération. Chaque épisode a donc sa propre barre, dans
  // son bloc (`renderEpisodeReview`) : à l'intérieur d'un épisode le champ est dit une fois, et une
  // requête par épisode est le plus grand geste qui ne mente sur aucune date.
  const profileKeys = useMemo(
    () => [
      ...profileFieldKeys(projection.pathway, schema, hiddenFieldKeys),
      ...problemFieldKeys(projection.pathway, schema, hiddenFieldKeys),
    ],
    [projection.pathway, schema, hiddenFieldKeys],
  );

  // The two answers the bar cannot find on its own on this surface: the gesture is the one the server
  // serves beside the value at the cursor's date (the projected patient's feedbacks are empty by
  // construction), and a confirmation is dated by the FACT, not by the click.
  const gestures = useMemo<FieldGestureResolver>(
    () => ({
      isPending: (key) => !(key in projection.gestures),
      effectiveDateOf: (key) => projectedDates[key] ?? at ?? todayIso,
    }),
    [projection.gestures, projectedDates, at, todayIso],
  );

  /** La barre qui clôt UN épisode — la même que celle du dossier, sur le périmètre d'un acte.
   *
   *  Elle reçoit les moments que le bloc vient de dessiner, filtre compris : ce qui est balayé est ce
   *  qui a été lu. Trois choses la distinguent de celle du profil, et elles viennent toutes de ce que
   *  la ligne d'épisode fait déjà champ par champ :
   *
   *   * la valeur balayée est celle du MOMENT, pas celle du patient projeté — d'où le patient
   *     synthétique ci-dessous, qui porte la valeur de l'épisode pour chacun de ses champs ;
   *   * the gesture is the one the server serves for THIS episode — otherwise a validation of the
   *     revision would put out the check of the first operation;
   *   * chaque confirmation part datée du jour de l'acte ET portant le document qui l'atteste, comme
   *     la coche de la ligne. C'est ce qui rend le balayage indiscernable de N clics.
   */
  const renderEpisodeReview: RenderEpisodeReviewFn = (episodeId, moments) => {
    if (moments.length === 0) return null;
    const byKey = new Map<string, EpisodeMoment>(
      moments.map((moment) => [moment.fieldKey, moment]),
    );
    // Le dossier tel que CET épisode le raconte. Un champ n'y figure qu'une fois — c'est ce qui
    // rend le balayage possible ici et impossible sur le dossier entier.
    const episodePatient = {
      ...displayPatient,
      ...Object.fromEntries(moments.map((moment) => [moment.fieldKey, moment.value])),
    } as MageXPatientWithFeedbacks;
    return (
      <ValidateRemainingCard
        // Comme les lignes : `ValidateRemainingCard` fige son décompte au montage, donc la barre se
        // remonte à chaque projection — curseur, correction, balayage.
        key={`${episodeId}@${projection.version}`}
        patient={episodePatient}
        schema={schema}
        fieldKeys={moments.map((moment) => moment.fieldKey)}
        loadPatient={loadPatient}
        processed={processedHere}
        variant="inline"
        actionLabel={<Trans>Valider les champs de cet épisode</Trans>}
        gestures={{
          isPending: (key) => {
            const moment = byKey.get(key);
            return moment !== undefined && moment.gesture === null;
          },
          effectiveDateOf: (key) => byKey.get(key)?.effectiveDate ?? null,
          scopeDocIdOf: (key) => byKey.get(key)?.scopeDocId ?? null,
        }}
        scopeHint={
          <Trans>Chaque confirmation sera datée du moment qui l'a établie.</Trans>
        }
      />
    );
  };

  /** Un champ d'ontologie, avec son encart, son crayon et sa coche — le SEUL rendu de valeur de la
   *  page, profil et épisode compris.
   *
   *  `override` est ce qui permet à un champ d'épisode d'être rendu par ce même composant : la ligne
   *  lit normalement la valeur sur le patient projeté, et un épisode a besoin qu'elle lise la valeur
   *  que CE moment a établie. Le reste suit — graphe de score au survol, validation, correction —
   *  sauf la date décrite par la correction, qui est celle du curseur dans le profil et celle du
   *  moment dans l'épisode. Sans ça, corriger la latéralité d'une opération de 2021 écrirait une
   *  valeur d'aujourd'hui et le passé deviendrait faux.
   */
  const renderOntologyField: RenderFieldFn = (key, override) => {
    const def = schema[key];
    const baseLabel = def?.title ?? mapFieldToLabel[key as keyof MageXPatient] ?? key;
    // Annoter sans cacher : la date clinique derrière la valeur. Dans un épisode elle est déjà portée
    // par la chronologie du moment, donc on ne la redit pas dans le libellé.
    const asOf = override ? null : projectedDates[key];
    const asOfLabel = asOf ? dayjs(asOf).format("DD/MM/YYYY") : "";
    const suffixes = [
      asOf ? t`au ${asOfLabel}` : null,
      override?.labelSuffix ?? null,
    ].filter(Boolean);
    const label = suffixes.length > 0 ? `${baseLabel} (${suffixes.join(" · ")})` : baseLabel;
    // The gesture the server serves for THIS episode, if any — placed by the rule that wrote the
    // episode's value (`correction_election`). A correction made on a revision says nothing of the
    // first operation.
    const momentGesture = override?.gesture ?? null;
    // Le patient tel que CETTE ligne doit le lire. Sans override c'est le patient projeté ; avec,
    // c'est lui augmenté de la seule valeur du moment — et, si un clinicien s'est prononcé sur ce
    // moment-là, de SA valeur plutôt que de celle du parcours.
    //
    // La distinction est ce qui manquait. Une ligne d'épisode lit `CareEventField`, que seule une
    // reconstruction du parcours réécrit : la correction partait bien au journal, et la ligne
    // continuait d'afficher la valeur extraite. Le médecin corrigeait « Oui » en « Non » et relisait
    // « Oui », sous une pastille qui lui annonçait un écart et l'invitait à rouvrir une modale dont
    // la sortie ne changeait rien. Le plancher de correction de la projection dit déjà cela pour le
    // bloc Profil ; l'épisode n'en avait pas l'équivalent.
    const patientForField = override
      ? ({
          ...displayPatient,
          [key]: momentGesture ? momentGesture.value : override.value,
        } as MageXPatientWithFeedbacks)
      : displayPatient;
    // Acte PLANIFIÉ dérivé par la projection ("<champ>_planifie") : un acte programmé postérieur à
    // l'acte réalisé courant. Cette clé n'existe pas dans le schéma — règle de rendu GÉNÉRIQUE par
    // convention de nommage, agnostique à la spécialité (comme le backend).
    const plannedRaw = override ? null : projectedFields?.[`${key}_planifie`];
    // A term of the field itself, shown in the reader's language like the value beside it.
    const planned =
      typeof plannedRaw === "string" && plannedRaw.trim()
        ? valueLabel(def?.value_labels, plannedRaw.trim())
        : null;
    // La date du FAIT que cette ligne montre : ce que la page DÉCRIT — à distinguer de l'endroit où
    // le curseur est posé, qui est ce qu'elle REGARDE. C'est la date du fait qui doit partir avec une
    // correction : corriger « Voie d'abord (au 15/06/2026) » corrige CE fait-là, pas l'état du patient
    // le jour du clic. Repli sur le curseur quand la projection ne date pas le champ — un champ encore
    // vide qu'un médecin renseigne pour la première fois n'a pas de date de fait.
    const factDate = override
      ? override.effectiveDate
      : (projectedDates[key] ?? at ?? todayIso);
    const passages: Passage[] = override
      ? (override.passages ?? [])
      : isProblemList(displayPatient[key as keyof MageXPatientWithFeedbacks])
        ? []
        : (projection.evidence[key] ?? []).map((item, index) => ({
            key: `${key}-${index}`,
            verbatim: item.evidence,
            sourceDocId: item.source_doc_id,
            date: item.doc_date,
            docType: item.doc_type,
          }));
    return (
      // Clé versionnée : `ValueAndPropertyDisplay` fige sa valeur au montage, donc la ligne se
      // remonte à chaque projection — curseur, correction, validation d'ensemble.
      <div key={`${key}@${projection.version}`}>
        <ValueAndPropertyDisplay
          schema={schema}
          loadPatient={loadPatient}
          label={label}
          property={key as keyof MageXPatient}
          type={def?.type}
          frozen={def?.frozen}
          patient={patientForField}
          // The dated projection has been read.
          processed={processedHere}
          // « Petit encart » : chaque item d'un array sur sa propre carte, jamais de concaténation par
          // virgules.
          cardVariant
          effectiveDate={factDate}
          // L'acte que cette ligne décrit, nommé plutôt que deviné. Une correction ne porte qu'une
          // date, et les bornes d'un épisode sont des intervalles : une ancre tombée dans la bande
          // ne dit pas de laquelle des deux opérations on parle. Seule la ligne d'un moment sait de
          // quel document elle vient ; le bloc Profil n'en sait rien et laisse la résolution à
          // l'ancre, comme avant.
          scopeDocId={override?.scopeDocId ?? null}
          // The gesture as the server serves it beside the value: at the cursor's date for the
          // profile (`GET /state`), for the episode inside an episode (`GET /pathway`). The page
          // neither elects it nor compares it — the server also says whether the value shown is
          // still the one the gesture judged.
          gesture={override ? momentGesture : (projection.gestures[key] ?? null)}
          questionnaire={questionnairesByField[key]}
        />
        <ValuePassages passages={passages} />
        {planned && (
          <div className="@container mt-1">
            <div className="flex @sm:justify-end">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-blue-50 px-2.5 py-1 text-xs font-medium text-primary">
                <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>
                  <Trans>Planifié</Trans> : {planned}
                </span>
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    // Pas de clé de version sur le bloc : le remontage est posé LIGNE par ligne. Ici, il emportait
    // aussi le sélecteur « Renseignés / Tous », qui revenait à son défaut à chaque correction.
    <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {/* La projection datée n'a pas pu être lue. Tous les champs cliniques de cette page en
            viennent, donc sans elle le dossier s'affiche VIDE — « Renseignés · 0 » se lit comme un
            constat clinique alors que c'est une lecture qui a échoué. */}
        {projection.failed && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-slate-700">
            <Trans>
              L'état daté de ce dossier n'a pas pu être chargé : les champs cliniques affichés sont
              vides pour cette raison, et non parce que le dossier ne les renseigne pas.
            </Trans>
          </div>
        )}

        {/* Le bandeau ne porte pas le diagnostic principal : c'est l'indication, donc une propriété de
            l'ÉPISODE. Hissée dans l'en-tête elle devenait l'union des raisons de tous les épisodes —
            sur un patient repris, deux diagnostics qui se contredisent côte à côte. */}
        <PatientHeaderCard
          patient={displayPatient}
          layout={headerLayout}
          schema={schema}
          // No episode at the cursor, no conflict: the declared badges speak instead of an empty banner.
          badgesSlot={
            projectedEpisodes.length > 0 ? (
              <PathwaySummary
                episodes={projectedEpisodes}
                openEpisodeId={openEpisode?.episode.id ?? null}
                onOpenEpisode={openEpisodeById}
                schema={schema}
              />
            ) : undefined
          }
          detailsSlot={
            projectedEpisodes.length > 0 ? (
              <PathwayLastContact episodes={projectedEpisodes} />
            ) : undefined
          }
        />

        <CarePathway
          pathway={projection.pathway}
          schema={schema}
          projectedFields={projectedFields}
          at={at}
          hiddenKeys={hiddenFieldKeys}
          openEpisodeId={openEpisode?.episode.id ?? null}
          onOpenEpisode={openEpisodeById}
          defaultToAllFields={inDataset}
          renderField={renderOntologyField}
          renderEpisodeReview={renderEpisodeReview}
        />

        {/* Clore le dossier en un geste : corriger ce qui est faux, et confirmer le reste une fois
            plutôt qu'une fois par champ. */}
        <ValidateRemainingCard
          patient={displayPatient}
          schema={schema}
          fieldKeys={profileKeys}
          loadPatient={loadPatient}
          processed={processedHere}
          gestures={gestures}
          scopeHint={
            <Trans>
              Les champs rattachés à un épisode se valident dans l'épisode, où chaque confirmation
              est datée de l'acte qu'elle décrit.
            </Trans>
          }
        />
      </div>

      {/* Une seule chronologie, et un seul rendu. Celle de l'épisode déplié — chaque moment daté par
          le document qui l'atteste — et, repliée dessous, la liste brute des rendez-vous de la
          source, dessinée par le MÊME composant. */}
      <div className="space-y-6">
        <Card
          data-tour="patient-chronology"
          className="border-l-4 border-l-primary"
        >
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center gap-2">
              <span>🗓️</span>
              <span>
                <Trans>Chronologie</Trans>
              </span>
              {openEpisode && (
                <span className="text-sm font-normal text-slate-400">
                  <Trans>Ép. {openEpisode.episode.rank}</Trans>
                </span>
              )}
            </CardTitle>
            {openEpisode?.episode.indication_label && (
              <p className="text-sm text-slate-500">
                {indicationLabel(openEpisode.episode, schema)}
              </p>
            )}
          </CardHeader>
          <CardContent>
            <ChronologyList
              items={episodeChronology(
                openEpisode?.events ?? [],
                eventKindLabel,
                schema,
              )}
              emptyLabel={
                openEpisode ? (
                  <Trans>Aucun moment à cette date.</Trans>
                ) : projectedEpisodes.length > 0 ? (
                  <Trans>Dépliez un épisode pour voir sa chronologie.</Trans>
                ) : (
                  <Trans>
                    Aucun épisode reconstruit : il n'y a pas encore de
                    chronologie.
                  </Trans>
                )
              }
            />

            {events.length > 0 && (
              <details className="mt-6 border-t border-slate-100 pt-4">
                <summary className="cursor-pointer text-sm text-slate-500 hover:text-primary">
                  <Trans>Agenda de la source · {events.length} rendez-vous</Trans>
                </summary>
                <div className="mt-4">
                  <ChronologyList
                    items={agendaItems}
                    emptyLabel={<Trans>Aucun rendez-vous.</Trans>}
                  />
                </div>
              </details>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
